'use client';

import { c as _c } from "react/compiler-runtime";
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { env } from '@repo/platform-env';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import type { FlattenedFunnelConfig } from '../../../config/flattened/types';
import { isFunnelComplete } from '../../funnel/completion';
import { saveFunnelSession } from '../../saveFunnelSession';
import type { SessionSyncResult } from '../../sessionWriteResult';
import { markLocalSessionComplete, readLocalSession } from '../../storage';
import { useAnswerDispatch } from '../hooks';
import { abandonFunnelRun } from './abandonFunnelRun';
import { resolveCompleteAction } from './resolveCompleteAction';
import { hasAuthTokenCookie } from './useHasAuthTokenCookie';

/**
 * Commits a finished funnel run — the only path that marks a run complete.
 *
 * Callers pass the answers in: the end-of-funnel caller's store snapshot
 * predates the final `SUBMIT_ANSWER` dispatch.
 *
 * Awaited by `useFinishFunnel`, so the run is marked complete locally before
 * the results page mounts and reads it.
 *
 * Never gated on being logged in — anonymous runs are recorded too.
 */
export function useCompleteFunnelRun(funnelFlattenedConfig, funnelConfigVersion) {
  const $ = _c(5);
  const dispatch = useAnswerDispatch();
  const router = useRouter();
  let t0;
  if ($[0] !== dispatch || $[1] !== funnelConfigVersion || $[2] !== funnelFlattenedConfig || $[3] !== router) {
    t0 = async answers => {
      const action = resolveCompleteAction({
        isRunComplete: isFunnelComplete(funnelFlattenedConfig, answers),
        localSession: readLocalSession(),
        sessionType: hasAuthTokenCookie() ? "authenticated" : "anonymous",
        isPersistenceEnabled: env.NEXT_PUBLIC_ENABLE_SESSION_PERSISTENCE
      });
      if (action.kind === "skip") {
        return "skipped";
      }
      const {
        result,
        sessionId
      } = await saveFunnelSession(answers, {
        status: "complete",
        funnelConfigVersion
      });
      if (sessionId && (result === "ok" || result === "already-complete")) {
        markLocalSessionComplete(sessionId, action.attemptedAs);
      }
      if (result === "version-mismatch") {
        abandonFunnelRun(dispatch, router);
      }
      return result;
    };
    $[0] = dispatch;
    $[1] = funnelConfigVersion;
    $[2] = funnelFlattenedConfig;
    $[3] = router;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}